import React, { useMemo } from "react";
import classNames from "classnames";

import { t } from "@web-solutions/module-localization";
import Analytics from "@web-solutions/module-analytics";

import {
  Title,
  Text,
  LinkButton,
  Modal,
  Image,
  Button,
} from "core/ui-elements";
import { useRemoteConfig } from "core/hooks/use-remote-config";
import { Theme } from "core/constants/theme";
import { ProductDetails } from "core/store/billing/selectors";
import { useThemeContext } from "core/hooks/use-theme-context";
import { PayPalBtn } from "core/payment/components/paypal";
import { type Subscription } from "core/interfaces/billing";

import { ReactComponent as IconCancel } from "./icons/cancel.svg";
import { IMAGES } from "./images";

import classes from "./style.module.scss";
import { InsufficientFunds } from "./components/302";
import { InvalidData } from "./components/201-208-209";
import { PaymentAmountLimit } from "./components/303";
import { SubscriptionLocked } from "./components/214";
import { CardIssuer } from "./components/003-306-403";
import { TokenNotFound } from "./components/701";
import { CardIssuerWithImage } from "./components/001-002-101-213-301-310-40x-501-602";
import { CardIssuerLaw } from "./components/304-305-308-408-601";
import { CardBrandNotSupported } from "./components/307";
import { CardIssuerInvalidFlow } from "./components/211-212-309-502-503-508-707";
import { CardIssuerEmailFooter } from "./components/2xx-5xx-7xx";
import { getContentByCode } from "./utils/get-content-by-code";
import { PaymentErrorState } from "core/payment/utils/payment-error-state";

const ERRORS_CONTENT = {
  insufficientFunds: InsufficientFunds,
  invalidData: InvalidData,
  paymentAmountLimit: PaymentAmountLimit,
  subscriptionLocked: SubscriptionLocked,
  cardIssuer: CardIssuer,
  tokenNotFound: TokenNotFound,
  cardIssuerWithImage: CardIssuerWithImage,
  cardIssuerLaw: CardIssuerLaw,
  cardBrandNotSupported: CardBrandNotSupported,
  cardIssuerInvalidFlow: CardIssuerInvalidFlow,
  cardIssuerEmailFooter: CardIssuerEmailFooter,
};

const images = {
  card: IMAGES.CARD_BG,
  billing: IMAGES.BILLING_BG,
};

const images_dark = {
  card: IMAGES.DARK_CARD_BG,
  billing: IMAGES.DARK_BILLING_BG,
};

type Props = {
  visible: boolean;
  product?: ProductDetails;
  onClose: (isPaypal?: boolean) => void;
  onSubmit: () => void;
  statusCode?: string | number;
  withStatusCode?: boolean;
  buttonTitle?: string;
  tKey?: string;
  onSkipClick?: () => void;
  errorMessage?: string;
  /**
   * One of the four payment-failure states. When set, the popup shows that
   * state's text and button instead of the code-keyed legacy content — one
   * layout for every failure, on the paywall and in the upsale alike.
   */
  errorState?: PaymentErrorState;
} & (
  | {
      withPayPal?: false;
    }
  | {
      withPayPal?: true;
      onPayPalSuccess: (p: Subscription) => void;
      onPayPalError: (e: any) => void;
      onPayPalSubmit: (formData: any) => void;
    }
);

const category = "error_popup";

const ErrorPopup: React.FC<Props> = (props) => {
  const {
    visible,
    product,
    onClose,
    onSubmit,
    withPayPal,
    buttonTitle,
    statusCode,
    tKey = "core.subscription.error_popup",
    onSkipClick,
    errorMessage,
    errorState,
  } = props;

  const { errorPopup } = useRemoteConfig();
  const { theme } = useThemeContext();
  const isDark = theme === Theme.dark ? true : false;

  const isInsufficientFunds = useMemo(() => {
    return (
      statusCode === "3.02" ||
      statusCode === "302" ||
      statusCode === 302 ||
      statusCode === 3.02
    );
  }, [statusCode]);

  const code = useMemo(() => {
    if (statusCode && isInsufficientFunds) {
      return "302";
    } else {
      return errorPopup?.type;
    }
  }, [statusCode, errorPopup?.type, isInsufficientFunds]);

  const Component = ERRORS_CONTENT[getContentByCode(statusCode)];

  // Prefix of the block the title / text / button are read from: the numbered
  // state when the caller resolved one, the legacy code-keyed block otherwise.
  const contentKey = errorState ? `${tKey}.states.${errorState}` : `${tKey}.${code}`;

  const handleClose = () => {
    onClose(withPayPal);
  };

  const handlePayPalSubmit = (formData: any) => {
    Analytics.trackEvent(category, "payment_submit", { mode: "paypal" });
    if (withPayPal) {
      props.onPayPalSubmit(formData);
    }
  };

  const handleSuccess = (p: Subscription) => {
    Analytics.trackEvent(category, "payment_success", { mode: "paypal" });
    if (withPayPal) {
      onClose();
      props.onPayPalSuccess(p);
    }
  };

  const handleError = (e: any) => {
    Analytics.trackEvent(category, "payment_error", { mode: "paypal" });
    if (withPayPal) {
      props.onPayPalError(e);
    }
  };


  return (
    <Modal
      isOpen={visible}
      className={classNames(classes.popup, { [classes.dark]: isDark })}
      overlayClassName={classNames(classes.overlay, { [classes.dark]: isDark })}
    >
      {errorPopup.isDetailedErrors && !withPayPal && !errorState ? (
        <Component onClick={onSubmit} />
      ) : (
        <>
          {/* <Image
            img={isDark ? images_dark[errorPopup?.type] : images[errorPopup?.type]}
            className={classes.img}
            alt='background'
          /> */}
          <div className={classes.header}>
            <Title className={classes.title}>
              {t(`${contentKey}.title`)}
            </Title>
            <IconCancel onClick={handleClose} className={classes.close} />
          </div>

          <Text className={classes.text}>
            {!errorState &&
            errorMessage === "Your transaction was declined due to insufficient funds in your account. Please use a different card or contact your bank."
              ? "Payment failed due to insufficient funds. Please update your card or try another payment method."
              : t(`${contentKey}.text`)}
          </Text>
          <>
            <Button
              onClick={onSubmit}
              flare={false}
              iconRight={false}
              className={classes.button}
              // The four states each carry their own call to action, so the
              // single config-wide button title must not override them.
              title={(errorState ? undefined : buttonTitle) || t(`${contentKey}.button`)}
            />
            {visible && withPayPal && product && (
              <div className={classes.paypal_wrap}>
                <div className={classes.or}>{t("core.payment_popup.or")}</div>
                <div className={classes.paypal}>
                  <PayPalBtn
                    onSuccess={handleSuccess}
                    onError={handleError}
                    product={product}
                    onSubmit={handlePayPalSubmit}
                  />
                </div>
              </div>
            )}
            {onSkipClick && !errorState && (
              <LinkButton className={classes.button} onClick={onSkipClick}>
                {t(`${tKey}.${code}.button_later`)}
              </LinkButton>
            )}
          </>
        </>
      )}
    </Modal>
  );
};

export default ErrorPopup;
